import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { IMGIX_DYNAMIC_BASE_URL } from '../../core/config';
import styles from './article-thumbnail.module.scss';

type Props = {
  articleId: string;
  hexCode: string | undefined;
  hasRendering: boolean;
  mini?: boolean;
};

const IMGIX_BASE_URL = `${IMGIX_DYNAMIC_BASE_URL}/3d/renderings/`;

const IMAGE_CONFIG = {
  mini: {
    widthPx: 120,
    heightFraction: 0.75,
  },
  default: {
    widthPx: 1160,
    heightFraction: 0.45,
  },
};

/**
 * Component to smoothly load an offline rendering image for a given articleId.
 * We only use a single source to have trivial smooth transition between hexCode and rendering.
 * @param articleId
 * @param hexCode
 * @param hasRendering
 * @param mini
 * @constructor
 */
export const ArticleThumbnail: React.FC<Props> = ({ articleId, hexCode, hasRendering, mini = false }) => {
  const [articleImage, setArticleImage] = useState<string | null>(null);
  useEffect(() => {
    if (!hasRendering) {
      setArticleImage(null);
      return;
    }
    const { widthPx, heightFraction } = mini ? IMAGE_CONFIG.mini : IMAGE_CONFIG.default;
    const url = `${IMGIX_BASE_URL}${articleId}.png?auto=format&bg=00000000&w=${widthPx}&rect=0,0,1.0,${heightFraction}`;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      setArticleImage(url);
    };
  }, [articleId, hasRendering, mini, setArticleImage]);

  return (
    <div
      className={classNames(styles.root, {
        [styles.noBg]: articleImage,
        [styles.mini]: mini,
      })}
      style={{
        '--igp-product-card-color': hexCode ?? '#fff',
      }}
    >
      {articleImage ? <img alt="" className={styles.articleImage} src={articleImage} /> : null}
    </div>
  );
};
